import React, { FC, useState, useMemo, useCallback } from 'react'
import { useField } from 'formik'
import {
  format,
  getDay,
  sub,
  set,
  add,
  differenceInCalendarDays,
  isSameMonth,
  getWeeksInMonth,
} from 'date-fns'

import { Stack } from '../Stack'
import { Button } from '../Button'
import { Element } from '../Element'
import { Text } from '../Text'

export const FormDatePicker: FC<{
  onChangeStartDate?: (newDate: Date) => any
  onChangeEndDate?: (newDate?: Date) => any
  startAtFieldName?: string
  endAtFieldName?: string
  selectingDate: 'start' | 'end'
}> = ({
  onChangeStartDate,
  onChangeEndDate,
  startAtFieldName = 'startAt',
  endAtFieldName = 'endAt',
  selectingDate,
}) => {
  const [startAtField, _startAtMeta, startAtHelpers] =
    useField(startAtFieldName)
  const [endAtField, _endAtMeta, endAtHelpers] = useField(endAtFieldName)

  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => {
    const initialDate = new Date(startAtField.value.getTime())

    initialDate.setDate(1)

    return initialDate
  })

  const month = useMemo(() => {
    return format(displayedMonth, 'MMMM')
  }, [displayedMonth])

  const setStartDate = useCallback(
    (newDate: Date) => {
      const newStartDate = set(startAtField.value, {
        year: newDate.getFullYear(),
        month: newDate.getMonth(),
        date: newDate.getDate(),
      })

      startAtHelpers.setValue(newStartDate)

      if (newStartDate.getMonth() !== displayedMonth.getMonth()) {
        const displayedMonth = set(newStartDate, { date: 1 })

        setDisplayedMonth(displayedMonth)
      }

      if (onChangeStartDate) {
        onChangeStartDate(newStartDate)
      }
    },
    [
      startAtField.value,
      startAtHelpers.setValue,
      setDisplayedMonth,
      onChangeStartDate,
    ]
  )

  const setEndDate = useCallback(
    (newDate?: Date) => {
      if (!newDate) {
        endAtHelpers.setValue(newDate)

        if (onChangeEndDate) {
          onChangeEndDate(newDate)
        }
        return
      }

      const newEndDate = set(endAtField.value, {
        year: newDate.getFullYear(),
        month: newDate.getMonth(),
        date: newDate.getDate(),
      })

      endAtHelpers.setValue(newEndDate)

      if (onChangeEndDate) {
        onChangeEndDate(newEndDate)
      }
    },
    [
      endAtField.value,
      endAtHelpers.setValue,
      setDisplayedMonth,
      onChangeEndDate,
    ]
  )

  return (
    <Stack gap={2}>
      <Stack direction='horizontal' justifyContent='space-between'>
        <Text>{month}</Text>

        <Stack direction='horizontal' gap={1}>
          <Button
            onPress={() => {
              setDisplayedMonth(sub(displayedMonth, { months: 1 }))
            }}
            variant='invisibleCompact'
          >
            <Text>←</Text>
          </Button>
          <Button
            onPress={() => {
              setDisplayedMonth(add(displayedMonth, { months: 1 }))
            }}
            variant='invisibleCompact'
          >
            <Text>→</Text>
          </Button>
        </Stack>
      </Stack>

      <Month
        startDate={startAtField.value}
        endDate={endAtField.value}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        displayedMonth={displayedMonth}
        selectingDate={selectingDate}
      />
    </Stack>
  )
}

const Month: FC<{
  startDate: Date
  endDate?: Date
  setStartDate: (newDate: Date) => any
  setEndDate: (newDate?: Date) => any
  displayedMonth: Date
  selectingDate: 'start' | 'end'
}> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  displayedMonth,
  selectingDate,
}) => {
  const firstDayOfCalendar = useMemo(() => {
    const dayOfWeek = getDay(displayedMonth)

    return sub(displayedMonth, { days: dayOfWeek })
  }, [displayedMonth])

  const selectDate = useCallback(
    (newDate: Date) => {
      switch (selectingDate) {
        case 'start': {
          setStartDate(newDate)

          if (endDate && endDate < newDate) {
            setEndDate(newDate)
          }
          return
        }

        default: {
          setEndDate(newDate)

          if (startDate > newDate) {
            setStartDate(newDate)
          }
        }
      }
    },
    [selectingDate, setStartDate, setEndDate, startDate, endDate]
  )

  return (
    <Stack gap={2} variant='calendarMonth'>
      <Stack>
        <Stack direction='horizontal' alignItems='flex-start'>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => {
            return (
              <Element
                key={index}
                tokens={{
                  width: 6,
                  height: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text variant='heading' size={4}>
                  {day}
                </Text>
              </Element>
            )
          })}
        </Stack>

        {Array.from({ length: getWeeksInMonth(displayedMonth) }).map(
          (_week, weekIndex) => {
            return (
              <Stack
                key={weekIndex}
                direction='horizontal'
                alignItems='flex-start'
              >
                {Array.from({ length: 7 }).map((_day, dayIndex) => (
                  <Day
                    key={dayIndex}
                    displayedMonth={displayedMonth}
                    firstDayOfCalendar={firstDayOfCalendar}
                    weekIndex={weekIndex}
                    dayIndex={dayIndex}
                    startDate={startDate}
                    endDate={endDate}
                    onPress={selectDate}
                  />
                ))}
              </Stack>
            )
          }
        )}
      </Stack>
    </Stack>
  )
}

const Day: FC<{
  firstDayOfCalendar: Date
  displayedMonth: Date
  weekIndex: number
  dayIndex: number

  startDate: Date
  endDate?: Date

  onPress?: (newDate: Date) => any
}> = ({
  firstDayOfCalendar,
  displayedMonth,
  weekIndex,
  dayIndex,

  startDate,
  endDate,

  onPress,
}) => {
  const workingDate = useMemo(() => {
    return add(firstDayOfCalendar, {
      days: weekIndex * 7 + dayIndex,
    })
  }, [firstDayOfCalendar, weekIndex, dayIndex])

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress(workingDate)
    }
  }, [onPress, workingDate])

  if (endDate) {
    if (differenceInCalendarDays(workingDate, endDate) === 0) {
      if (differenceInCalendarDays(workingDate, startDate) === 0) {
        return (
          <Button variant='calendarDaySelected' onPress={handlePress}>
            <Text variant='button' size={4}>
              {workingDate.getDate()}
            </Text>
          </Button>
        )
      }

      return (
        <Button variant='calendarDayEnd' onPress={handlePress}>
          <Text variant='button' size={4}>
            {workingDate.getDate()}
          </Text>
        </Button>
      )
    }

    if (differenceInCalendarDays(workingDate, startDate) === 0) {
      return (
        <Button variant='calendarDayStart' onPress={handlePress}>
          <Text variant='button' size={4}>
            {workingDate.getDate()}
          </Text>
        </Button>
      )
    }

    if (workingDate < endDate && workingDate > startDate) {
      return (
        <Button variant='calendarDayBetween' onPress={handlePress}>
          <Text variant='button' size={4}>
            {workingDate.getDate()}
          </Text>
        </Button>
      )
    }
  } else if (startDate) {
    if (differenceInCalendarDays(workingDate, startDate) === 0) {
      return (
        <Button variant='calendarDaySelected' onPress={handlePress}>
          <Text variant='button' size={4}>
            {workingDate.getDate()}
          </Text>
        </Button>
      )
    }
  }

  if (workingDate < new Date()) {
    return (
      <Button variant='calendarDayOutsideMonth'>
        <Text variant='calendarDayFaded' size={4}>
          {workingDate.getDate()}
        </Text>
      </Button>
    )
  }

  if (isSameMonth(displayedMonth, workingDate)) {
    return (
      <Button variant='calendarDay' onPress={handlePress}>
        <Text size={4}>{workingDate.getDate()}</Text>
      </Button>
    )
  }

  return (
    <Button variant='calendarDayOutsideMonth' onPress={handlePress}>
      <Text variant='calendarDayFaded' size={4}>
        {workingDate.getDate()}
      </Text>
    </Button>
  )
}
